/** Presupuestos agrupados por código: una fila por código con versión vigente. */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { MobileLayout } from '@/components/MobileLayout';
import { MobileCard } from '@/components/MobileCard';
import { StatusBadge } from '@/components/StatusBadge';
import { SearchInput } from '@/components/SearchInput';
import { CardListSkeleton } from '@/components/LoadingSkeleton';
import { EmptyState } from '@/components/EmptyState';
import { PullIndicator } from '@/components/PullIndicator';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';
import { useMobileBudgetsQuery } from '@/hooks/useMobileQuotations';
import { useMobileAuth } from '@/state/MobileAuthContext';
import { formatCurrency } from '@/utils/format';
import type { MobileBudget } from '@/hooks/useMobileQuotations';

function formatDate(value: string | undefined): string {
  if (!value) return '—';
  const date = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Estados que dejan de ser vigentes: la vigente es la mayor vN fuera de ellos. */
const NON_CURRENT_STATUSES = new Set(['superseded', 'cancelled']);

export type BudgetGroup = {
  key: string;
  budgetCode: string;
  current: MobileBudget;
  versions: number;
};

export function groupBudgetsByCode(items: MobileBudget[]): BudgetGroup[] {
  const byKey = new Map<string, MobileBudget[]>();
  for (const item of items) {
    const key = (item.budget_code ?? '').trim().toUpperCase();
    const list = byKey.get(key);
    if (list) list.push(item);
    else byKey.set(key, [item]);
  }
  const groups: BudgetGroup[] = [];
  for (const [key, rows] of byKey) {
    const sorted = [...rows].sort((a, b) => b.version_number - a.version_number);
    const current = sorted.find((row) => !NON_CURRENT_STATUSES.has(row.status)) ?? sorted[0];
    if (!current) continue;
    groups.push({ key, budgetCode: current.budget_code, current, versions: sorted.length });
  }
  return groups.sort((a, b) => a.budgetCode.localeCompare(b.budgetCode));
}

export function BudgetsPage() {
  const { user, hasPermission } = useMobileAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const budgetsQuery = useMobileBudgetsQuery();
  const { bindRef, pulling, refreshing } = usePullToRefresh({
    onRefresh: () => budgetsQuery.refetch(),
    disabled: user?.active_tenant_id == null || !hasPermission('budgets.view'),
  });

  const filteredItems = useMemo(() => {
    const items = budgetsQuery.data?.items ?? [];
    const mine = showAll
      ? items
      : items.filter((item) => item.created_by && item.created_by === user?.id);
    if (!search.trim()) return mine;
    const q = search.trim().toLowerCase();
    return mine.filter(
      (item) =>
        item.budget_code.toLowerCase().includes(q) ||
        (item.customer_requirement?.client?.legal_name ?? '').toLowerCase().includes(q) ||
        (item.customer_requirement?.code ?? '').toLowerCase().includes(q),
    );
  }, [budgetsQuery.data, search, showAll, user?.id]);

  const groups = useMemo(() => groupBudgetsByCode(filteredItems), [filteredItems]);
  const visibleCount = showHistory ? filteredItems.length : groups.length;

  return (
    <MobileLayout
      title="Presupuestos"
      subtitle={`${visibleCount} de ${budgetsQuery.data?.total ?? 0} en total`}
    >
      <div ref={bindRef} className="space-y-4">
        <PullIndicator pulling={pulling} refreshing={refreshing} />

        <div
          role="radiogroup"
          aria-label="Filtrar presupuestos por autor"
          className="flex items-center gap-1 rounded-full bg-bg-tertiary p-1"
        >
          {(
            [
              { value: false, label: 'Solo míos' },
              { value: true, label: 'Todos' },
            ] as const
          ).map((option) => (
            <button
              key={option.label}
              type="button"
              role="radio"
              aria-checked={showAll === option.value}
              onClick={() => setShowAll(option.value)}
              className={clsx(
                'flex h-11 min-h-[44px] flex-1 items-center justify-center rounded-full text-sm font-semibold transition-all active:scale-[0.97]',
                showAll === option.value
                  ? 'bg-white text-text-primary shadow-card'
                  : 'text-text-secondary hover:text-text-primary',
              )}
            >
              {option.label}
            </button>
          ))}
        </div>

        <button
          type="button"
          role="switch"
          aria-checked={showHistory}
          onClick={() => setShowHistory((next) => !next)}
          className="flex min-h-[44px] w-full items-center gap-3 rounded-card bg-white px-4 py-2 shadow-card"
        >
          <span
            aria-hidden="true"
            className={clsx(
              'relative h-7 w-12 shrink-0 rounded-full transition-colors',
              showHistory ? 'bg-primary' : 'bg-border',
            )}
          >
            <span
              className={clsx(
                'absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all',
                showHistory ? 'left-[22px]' : 'left-0.5',
              )}
            />
          </span>
          <span className="min-w-0 flex-1 truncate text-left text-sm font-semibold text-text-primary">
            Ver históricas
          </span>
          <span className="shrink-0 text-xs text-text-tertiary">
            {showHistory ? 'Todas las versiones' : 'Solo vigentes'}
          </span>
        </button>

        <SearchInput value={search} onChange={setSearch} placeholder="Buscar por código o cliente..." />

        {budgetsQuery.isPending && <CardListSkeleton count={4} />}

        {budgetsQuery.isError && (
          <EmptyState
            icon="error_outline"
            title="No se pudo cargar"
            description="Verifica tu conexión e intenta de nuevo."
            actionLabel="Reintentar"
            onAction={() => budgetsQuery.refetch()}
          />
        )}

        {!budgetsQuery.isPending && !budgetsQuery.isError && visibleCount === 0 && (
          <EmptyState
            icon={search || showAll ? 'search_off' : 'calculate'}
            title={search ? 'Sin resultados' : 'Sin presupuestos'}
            description={
              search
                ? 'Ninguno coincide con tu búsqueda.'
                : showAll
                  ? 'Aún no hay presupuestos.'
                  : 'Aún no tienes presupuestos asignados.'
            }
          />
        )}

        <div className="space-y-3">
          {!showHistory &&
            groups.map((group) => {
              const item = group.current;
              return (
                <MobileCard
                  key={group.key}
                  title={`${item.budget_code} · v${item.version_number}`}
                  subtitle={group.versions > 1 ? `${group.versions} versiones` : undefined}
                  icon="calculate"
                  iconColor="info"
                  rightContent={<StatusBadge status={item.status} size="sm" />}
                  metadata={[
                    ...(item.customer_requirement?.client?.legal_name
                      ? [{ label: 'Cliente', value: item.customer_requirement.client.legal_name }]
                      : []),
                    ...(item.total !== undefined
                      ? [{ label: 'Total', value: formatCurrency(item.total, item.currency ?? 'USD') }]
                      : []),
                    ...(item.budget_date
                      ? [{ label: 'Fecha', value: formatDate(item.budget_date) }]
                      : []),
                  ]}
                  onClick={() => navigate(`/m/budgets/${item.id}`)}
                />
              );
            })}
          {showHistory &&
            filteredItems.map((item) => (
              <MobileCard
                key={item.id}
                title={`${item.budget_code} · v${item.version_number}`}
                icon="calculate"
                iconColor="info"
                rightContent={<StatusBadge status={item.status} size="sm" />}
                metadata={[
                  ...(item.customer_requirement?.client?.legal_name
                    ? [{ label: 'Cliente', value: item.customer_requirement.client.legal_name }]
                    : []),
                  ...(item.total !== undefined
                    ? [{ label: 'Total', value: formatCurrency(item.total, item.currency ?? 'USD') }]
                    : []),
                  ...(item.budget_date
                    ? [{ label: 'Fecha', value: formatDate(item.budget_date) }]
                    : []),
                ]}
                onClick={() => navigate(`/m/budgets/${item.id}`)}
              />
            ))}
        </div>
      </div>
    </MobileLayout>
  );
}
